// src/helpers/downloadDgkExcel.ts
import * as XLSX from "xlsx";

type AnyObj = Record<string, any>;

const safe = (v: any) => (v === undefined || v === null ? "" : String(v));

/**
 * Build rows from redux dgkData. Supports both shapes:
 * - dgkData.pages[] (recommended)
 * - dgkData.form (older/simple)
 */
export function buildDgkExcelRows(dgkData: AnyObj) {
  const type = String(dgkData?.type || "").toUpperCase();

  if (type === "YGB") {
    const header = [
      "Müqavilə nömrəsi",
      "Müqavilə tarixi",
      "Vəsaiti alanın ölkəsi",
      "Vəsaiti alan",
      "GB üzrə köçürülən məbləğ",
      "Valyuta",
      "Hesabın valyutası üzrə mübadilə",
    ];

    const pages: AnyObj[] = Array.isArray(dgkData?.pages) ? dgkData.pages : [];

    // Fallback (old payload)
    if (!pages.length && dgkData?.form) {
      const f = dgkData.form;
      return {
        sheetName: "ExcelFile",
        header,
        rows: [
          [
            safe(f.contractNo),
            safe(f.contractDate),
            safe(f.receiverCountry || f.receiverCountryId),
            safe(f.receiverName),
            safe(f.transferredAmount),
            safe(f.currencyType),
            safe(f.exchange),
          ],
        ],
      };
    }

    const rows = pages.map((p) => {
      const f = p?.form || p?.formValues || {};
      const summary = p?.summary || {};
      return [
        safe(f.contractNo),
        safe(f.contractDate),
        safe(summary.receiverCountry || f.receiverCountry || f.receiverCountryId),
        safe(f.receiverName),
        safe(f.transferredAmount),
        safe(f.currencyType),
        safe(f.exchange),
      ];
    });

    return { sheetName: "ExcelFile", header, rows };
  }

  if (type === "AVANS") {
    const header = [
      "Müqavilə nömrəsi",
      "Tarix",
      "Hesab nömrəsi",
      "Ölkə",
      "Qarşı tərəf",
      "Məbləğ",
      "Valyuta",
      "Hesabın valyutası üzrə mübadilə",
    ];

    // Avans usually saved as dgkData.form with dynamic keys
    const values: AnyObj = dgkData?.form || {};
    const indices:any = new Set<number>();

    Object.keys(values).forEach((k) => {
      const m = k.match(/-(\d+)$/);
      if (m) indices.add(Number(m[1]));
    });

    const sorted = [...indices].sort((a, b) => a - b);

    const rows = sorted.map((i) => [
      safe(values[`contNo-${i}`]),
      safe(values[`date-${i}`]),
      safe(values[`accountNo-${i}`]),
      safe(values[`avansReceiverCountryId-${i}`] || values[`country-${i}`]),
      safe(values[`avansReceiver-${i}`]),
      safe(values[`avansTransferredAmount-${i}`]),
      safe(values[`avansCurrencyType-${i}`]),
      safe(values[`avansExchange-${i}`]),
    ]);

    return { sheetName: "ExcelFile", header, rows };
  }

  // AKT
  if (type === "AKT") {
    const header = [
      "Aktın tarixi",
      "Aktın nömrəsi",
      "Ümumi sənədin məbləği",
      "Köçürülən məbləğ",
      "Valyuta",
      "Hesabın valyutası üzrə mübadilə",
      "AKT üzrə alan tərəf",
      "Xidmət göstərən",
      "Mal və xidmət haqqında məlumat",
      "Ölkə",
      "Qeyd",
    ];

    const pages: AnyObj[] = Array.isArray(dgkData?.pages) ? dgkData.pages : [];

    // Fallback
    if (!pages.length && dgkData?.form) {
      const f = dgkData.form;
      const noNumber = !!f.noActDocNumber;
      return {
        sheetName: "ExcelFile",
        header,
        rows: [
          [
            safe(f.actDocDate),
            noNumber ? "" : safe(f.actDocNumber),
            safe(f.actDocCost),
            safe(f.actTransCost),
            safe(f.currencyType),
            safe(f.actExchange),
            safe(f.actSender),
            safe(f.actServiceProvider ? f.actServiceProviderName : ""),
            safe(f.goodServiceInfo),
            safe(f.countryId),
            safe(f.note),
          ],
        ],
      };
    }

    const rows = pages.map((p) => {
      const f = p?.form || p?.formValues || {};
      const noNumber = !!f.noActDocNumber;
      return [
        safe(f.actDocDate),
        noNumber ? "" : safe(f.actDocNumber),
        safe(f.actDocCost),
        safe(f.actTransCost),
        safe(f.currencyType),
        safe(f.actExchange),
        safe(f.actSender),
        safe(f.actServiceProvider ? f.actServiceProviderName : ""),
        safe(f.goodServiceInfo),
        safe(f.countryId),
        safe(f.note),
      ];
    });

    return { sheetName: "ExcelFile", header, rows };
  }

  return null;
}

export function downloadDgkExcel(dgkData: AnyObj) {
  const built = buildDgkExcelRows(dgkData);
  if (!built) return;

  const { sheetName, header, rows } = built;

  const aoa = [header, ...rows];
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName);

  const type = String(dgkData?.type || "DGK").toUpperCase();
  const filename = type === "YGB" ? "Əlavə olunmuş YGB siyahısı.xlsx" : type === "AVANS" ? "Avansların siyahısı.xlsx" : "Aktların siyahısı.xlsx";

  XLSX.writeFile(wb, filename);
}